import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert,
  LinearProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams } from "react-router-dom";
import TinyEditor from "../../../custom-ckeditor/TinyEditor";
import ItemService from "../../../apis/ItemService";
import {
  BLOOMS_TAXONOMY_SKILLS,
  normalizeSkillTypeValue,
} from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";

const RankingItemDetail = () => {
  const { itemId } = useParams();
  const isNew = !itemId || itemId === "new";

  const [item, setItem] = useState({
    Type: "Ranking",
    Name: "",
    points: 1,
    skilltype: "",
    itemcat: "",
    Direction: "LTR",
    Title: "",
    Options: [{ id: 1, Value: "", Rationale: "" }],
  });

  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [isRtl, setIsRtl] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const detectIsArabic = (text) => /[\u0600-\u06FF]/.test(text);

  // 🟢 Load taxonomy + existing item (if editing)
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [taxonomyResponse, itemResponse] = await Promise.all([
          ItemService.fetchTaxonomy("arabic"),
          isNew ? Promise.resolve(null) : ItemService.fetchItemById(itemId),
        ]);

        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomyResponse));

        if (itemResponse?.item) {
          const loaded = itemResponse.item;
          setItem({
            Type: loaded.Type || "Ranking",
            Name: loaded.Name || "",
            points: loaded.points ?? 1,
            skilltype: loaded.skilltype || "",
            itemcat: loaded.itemcat || "",
            Direction: loaded.Direction || "LTR",
            Title: loaded.Title || "",
            Options:
              loaded.Options?.length > 0
                ? loaded.Options.map((o, i) => ({
                    id: o.id ?? i + 1,
                    Value: o.Value ?? "",
                    Rationale: o.Rationale ?? "",
                  }))
                : [{ id: 1, Value: "", Rationale: "" }],
          });
        }
      } catch (err) {
        console.error("❌ Failed to load Ranking item:", err);
        setSaveError("Failed to load Ranking item.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [itemId, isNew]);

  // 🧭 Detect Arabic automatically
  useEffect(() => {
    const firstValue = item.Options?.[0]?.Value || "";
    if (firstValue) setIsRtl(detectIsArabic(firstValue));
  }, [item.Options]);

  const handleOptionChange = (index, field, value) => {
    setItem((prev) => {
      const opts = [...prev.Options];
      opts[index] = { ...opts[index], [field]: value };
      return { ...prev, Options: opts };
    });
  };

  const addOption = () =>
    setItem((prev) => ({
      ...prev,
      Options: [
        ...prev.Options,
        { id: prev.Options.length + 1, Value: "", Rationale: "" },
      ],
    }));

  const deleteOption = (index) =>
    setItem((prev) => ({
      ...prev,
      Options: prev.Options.filter((_, i) => i !== index),
    }));

  // 🟢 Save (create/update)
  const handleSave = useCallback(async () => {
    if (!item.Name || !item.itemcat || !item.skilltype) {
      setSaveError("Please fill all required fields.");
      return;
    }

    const timestamp = new Date().toISOString();

    const payload = {
      PK: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
      SK: `item#${itemId || "new"}`,
      createdBy: "",
      updatedBy: "",
      dateCreated: timestamp,
      dateUpdated: timestamp,
      GPK1: "item",
      GSK1: `item#${itemId || "new"}`,
      itemcat: item.itemcat,
      skilltype: item.skilltype,
      Name: item.Name,
      Options: item.Options.map((opt, i) => ({
        id: i + 1,
        Rationale: opt.Rationale || "",
        Value: opt.Value || "",
      })),
      points: item.points,
      Title:
        item.Title ||
        "<div>Arrange the words to form the correct sentence</div>",
      Type: "Ranking",
      version: "v1",
    };

    try {
      setSaving(true);
      await ItemService.saveItem(payload, isNew ? undefined : itemId);
      setSaveSuccess(true);
      setSaveError(null);
    } catch (err) {
      setSaveError(err?.message || "Failed to save item.");
    } finally {
      setSaving(false);
    }
  }, [item, itemId, isNew]);

  const jsonPreview = useMemo(() => JSON.stringify(item, null, 2), [item]);

  if (loading)
    return (
      <Paper sx={{ p: 3 }}>
        <LinearProgress />
        <Typography>Loading Ranking item...</Typography>
      </Paper>
    );

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        {/* === Header === */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label="Ranking" color="secondary" />
            <Typography variant="h6" fontWeight={600}>
              {isNew ? "Create Ranking Item" : "Edit Ranking Item"}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => setPreviewDialogOpen(true)}
            >
              Preview
            </Button>
            <Button variant="outlined" onClick={() => setJsonDialogOpen(true)}>
              View JSON
            </Button>
          </Stack>
        </Stack>

        {saveSuccess && (
          <Alert severity="success">Item saved successfully!</Alert>
        )}
        {saveError && <Alert severity="error">{saveError}</Alert>}

        {/* === Basic Fields === */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Item Name"
              fullWidth
              required
              value={item.Name}
              onChange={(e) => setItem((p) => ({ ...p, Name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Points"
              type="number"
              fullWidth
              value={item.points}
              onChange={(e) =>
                setItem((p) => ({ ...p, points: Number(e.target.value) }))
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth required>
              <InputLabel>Skill Type</InputLabel>
              <Select
                value={item.skilltype}
                label="Skill Type"
                onChange={(e) =>
                  setItem((p) => ({
                    ...p,
                    skilltype: normalizeSkillTypeValue(e.target.value),
                  }))
                }
              >
                {BLOOMS_TAXONOMY_SKILLS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Item Category</InputLabel>
              <Select
                value={item.itemcat}
                label="Item Category"
                onChange={(e) =>
                  setItem((p) => ({ ...p, itemcat: e.target.value }))
                }
              >
                {taxonomyOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* === Prompt === */}
        <Divider />
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Instructions (Title)
          </Typography>
          <TinyEditor
            value={item.Title || ""}
            onChange={(v) => setItem((p) => ({ ...p, Title: v }))}
            placeholder="Write your question or instructions..."
            minHeight={250}
          />
        </Box>

        {/* === RTL Toggle === */}
        <FormControlLabel
          control={
            <Switch checked={isRtl} onChange={() => setIsRtl((p) => !p)} />
          }
          label="Right-to-left direction"
        />

        {/* === Options === */}
        <Divider />
        <Typography variant="subtitle1" fontWeight={600}>
          Ranking Items
        </Typography>
        {item.Options.map((opt, i) => (
          <Stack key={i} spacing={1} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label={`Item ${i + 1}`}
              value={opt.Value}
              onChange={(e) => handleOptionChange(i, "Value", e.target.value)}
              inputProps={{
                dir: isRtl ? "rtl" : "ltr",
                style: { textAlign: isRtl ? "right" : "left" },
              }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Rationale"
              value={opt.Rationale}
              onChange={(e) =>
                handleOptionChange(i, "Rationale", e.target.value)
              }
            />
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => deleteOption(i)}
              sx={{ alignSelf: "flex-end", mt: -1 }}
            >
              Remove
            </Button>
          </Stack>
        ))}
        <Button startIcon={<AddIcon />} onClick={addOption}>
          Add Item
        </Button>

        {/* === Save === */}
        <Stack direction="row" justifyContent="flex-end">
          <Button
            startIcon={<SaveIcon />}
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Item"}
          </Button>
        </Stack>
      </Stack>

      {/* === JSON Dialog === */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              fontSize: 13,
            }}
          >
            {jsonPreview}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* === PREVIEW Dialog === */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Ranking Preview</DialogTitle>
        <DialogContent dividers>
          <Stack
            spacing={1.5}
            sx={{
              direction: isRtl ? "rtl" : "ltr",
              textAlign: isRtl ? "right" : "left",
            }}
          >
            {item.Options.map((opt, i) => (
              <Box
                key={i}
                sx={{
                  p: 1.5,
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  backgroundColor: "#fafafa",
                }}
              >
                {opt.Value || <em>(empty)</em>}
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RankingItemDetail;
